import { FC, ReactElement, ReactNode } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import { Link, To } from 'react-router-dom';
import { isEmpty, isNumber } from 'lodash';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { Card, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  Contributors,
  Icon,
  PublicationInfo,
  SearchInstanceDto,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import { getFormattedDate } from '@app/utils';
import { sharedMessages } from '@app/translations';

import './DashboardRecordCard.scss';

interface DashboardRecordCardProps {
  title: string;
  titleLink: To;
  tools: ReactElement;
  contributors?: { name: string }[];
  subTitle?: string;
  date?: string;
  timestamp?: string;
  resultsCount?: number;
  tags?: ReactNode[];
  actionButton?: ReactElement;
  publication?: SearchInstanceDto['publication'];
}

const cnBemRecordSummary = createBemBlockBuilder(['recordSummary']);
const cnBem = createBemBlockBuilder(['dashboardRecordCard']);

export const DashboardRecordCard: FC<DashboardRecordCardProps> = ({
  title,
  titleLink,
  tools = [],
  contributors,
  subTitle,
  timestamp,
  resultsCount,
  tags,
  actionButton,
  publication,
}) => {
  const { $t } = useIntl();
  const { locale } = useAppContext();

  const isNonExistingRecord = isEmpty(title);

  return (
    <Card border="bottom">
      <div data-auto="dashboard-record-card" className={cnBem('__container')}>
        {subTitle && (
          <div
            className={cnBemRecordSummary('__type')}
            data-auto="dashboard-record-subtitle"
          >
            {subTitle}
          </div>
        )}
        <h2 className={cnBem('__title')}>
          {isNonExistingRecord ? (
            $t({
              id: 'saved.itemNoLongerAvailable',
              defaultMessage: 'Item no longer available',
            })
          ) : (
            <Link tabIndex={0} to={titleLink}>
              {title}
            </Link>
          )}
        </h2>
        {contributors && (
          <div
            className={cnBem('__contributors')}
            data-auto="dashboard-record-contributors"
          >
            <Contributors
              contributors={contributors}
              cnBemModalOrPlacement={cnBemRecordSummary}
            />
          </div>
        )}
        {publication?.[0]?.dateOfPublication && (
          <div
            className={cnBem('__publication')}
            data-auto="dashboard-record-publication"
          >
            <PublicationInfo publication={publication} />
          </div>
        )}
        {timestamp && (
          <div className={cnBem('__time')}>
            <div data-auto="dashboard-record-timestamp">
              <Icon icon={faClock}>
                {getFormattedDate({
                  date: timestamp,
                  locale,
                })}
              </Icon>
            </div>
            {isNumber(resultsCount) && (
              <div data-auto="dashboard-record-results-count">
                {$t(sharedMessages.results, {
                  number: <FormattedNumber value={resultsCount} />,
                })}
              </div>
            )}
          </div>
        )}
        {tags && (
          <div className={cnBem('__tags')}>
            {tags.map(tag => (
              <div key={tag?.toString()} data-auto="dashboard-record-tag">
                {tag}
              </div>
            ))}
          </div>
        )}
        {actionButton && (
          <div className={cnBem('__actionButton')}>{actionButton}</div>
        )}
        <div className={cnBem('__toolsWrapper')}>{tools}</div>
      </div>
    </Card>
  );
};
